import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Alert } from "antd";
import { LuMegaphone, LuX } from "react-icons/lu";

/**
 * Announcement banner shown above the header.
 *
 * Everything user-facing lives in ANNOUNCEMENT below so the copy can be
 * swapped without touching the markup. Bump `id` whenever the message
 * changes — dismissal is stored per-id, so a new id re-shows the banner to
 * users who dismissed the previous one.
 *
 * `content` is keyed by language code. The banner renders the entry matching
 * the logged-in user's `preferredLanguageCode` (ME query), falling back to
 * DEFAULT_LANG for any language we haven't translated. Adding a language is
 * just another key here — no component change.
 */
const DEFAULT_LANG = "en";

export const ANNOUNCEMENT = {
  id: "2026-09-email-delivery-delay",
  // Set to false to hide the banner entirely without removing the code.
  enabled: false,
  dismissible: true,
  // antd Alert variant: "warning" | "info" | "error" | "success".
  type: "warning",
  // Optional call-to-action rendered inline after the message. Set to null for
  // no link. Only `label` is a link — the lead-in word ("Contact" / "Contacta
  // a") is per-language plain text, so the highlight lands on the address
  // alone.
  cta: { href: "mailto:support@engagedai.io", label: "support@engagedai.io" },
  content: {
    en: {
      title: "",
      message:
        "Email delivery is temporarily delayed due to high volume. Your emails are safely queued and will be sent automatically. No action is needed. Questions?",
      ctaPrefix: "Contact",
    },
    es: {
      title: "",
      message:
        "La entrega de correos electrónicos está temporalmente retrasada debido al alto volumen. Tus correos están en cola de forma segura y se enviarán automáticamente. No necesitas realizar ninguna acción. ¿Preguntas?",
      ctaPrefix: "Contacta a",
    },
  },
};

/**
 * Resolve the copy for a language code. Region suffixes and casing are
 * tolerated ("es-MX", "es_ES", "ES" all resolve to "es") — the same
 * normalisation `normalizeLanguageCode` in languageSlice applies, kept local so
 * this banner isn't limited to the codes that slice ships bundles for.
 */
export const resolveAnnouncementContent = (announcement, languageCode) => {
  const { content = {} } = announcement || {};
  const base = String(languageCode || "")
    .trim()
    .toLowerCase()
    .split(/[-_]/)[0];
  return content[base] || content[DEFAULT_LANG] || {};
};

/**
 * antd's "Sunrise Yellow" ramp, a few steps in from the stock `warning` Alert
 * (gold-1 #fffbe6 — nearly white, easy to scroll past at the top of the app).
 * Values are inlined rather than imported from @ant-design/colors: that package
 * is only a transitive dependency of antd here, not a direct one.
 */
const WARNING_TONE = {
  background: "#ffe58f", // yellow-2
  border: "#FADB14", // yellow-6
  text: "#000000",
};

const STORAGE_KEY_PREFIX = "announcementDismissed:";
const CSS_VAR = "--announcement-height";

const storageKey = id => `${STORAGE_KEY_PREFIX}${id}`;

const isDismissed = id => {
  try {
    return localStorage.getItem(storageKey(id)) === "true";
  } catch (e) {
    // Private mode / blocked storage — just show the banner.
    return false;
  }
};

const AnnouncementBanner = ({
  announcement = ANNOUNCEMENT,
  languageCode = DEFAULT_LANG,
  // Escape hatch for hosts that render the banner inside a padded container
  // (the extension's #app-container has py-[5px]) and need it to sit flush.
  className = "",
}) => {
  const { id, enabled, cta, dismissible, type = "warning" } = announcement;
  const { title, message, ctaPrefix } = resolveAnnouncementContent(announcement, languageCode);
  const isWarning = type === "warning";
  const barRef = useRef(null);
  // Start hidden so a dismissed banner never flashes on first paint; the
  // effect below flips it on once localStorage has been read.
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (enabled && !isDismissed(id)) {
      setVisible(true);
    }
  }, [id, enabled]);

  // Publish the rendered height as a CSS variable so the layout's
  // `calc(100vh - …)` content column stays exact instead of overflowing.
  useLayoutEffect(() => {
    const root = document.documentElement;
    if (!visible || !barRef.current) {
      root.style.setProperty(CSS_VAR, "0px");
      return undefined;
    }
    const sync = () => {
      const h = barRef.current?.offsetHeight || 0;
      root.style.setProperty(CSS_VAR, `${h}px`);
    };
    sync();
    // Guarded like the other ResizeObserver users in the codebase (see
    // useChatScroll / TruncatedTooltip): a throw here happens during commit,
    // which the root ErrorBoundary would turn into a blank app over a banner.
    // Without the observer the var just keeps its first measurement — the bar
    // still renders, it only stops re-measuring on reflow.
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(sync);
    observer.observe(barRef.current);
    return () => {
      observer.disconnect();
      root.style.setProperty(CSS_VAR, "0px");
    };
  }, [visible, message, title]);

  const handleDismiss = () => {
    setVisible(false);
    try {
      localStorage.setItem(storageKey(id), "true");
    } catch (e) {
      // Dismissal simply won't persist across reloads.
    }
  };

  if (!visible) return null;

  return (
    <div ref={barRef} className={`relative z-[70] w-full ${className}`}>
      <Alert
        // `banner` drops the rounded corners and side borders so the bar sits
        // flush against the top of the app, which is exactly the shape we want.
        banner
        type={type}
        // Only the warning variant gets the deepened tone; any other variant
        // the config asks for keeps antd's stock colors.
        style={
          isWarning
            ? {
                backgroundColor: WARNING_TONE.background,
                borderColor: WARNING_TONE.border,
              }
            : undefined
        }
        // `banner` implies showIcon, so this has to be an explicit false —
        // otherwise antd renders its stock warning icon in a left-hand column,
        // which both duplicates the megaphone and pushes the centered text
        // off-centre. The megaphone lives inside the centered block instead,
        // so icon + text center together.
        showIcon={false}
        closable={dismissible}
        closeIcon={<LuX size={14} />}
        onClose={handleDismiss}
        message={
          <span
            // `flex` so the icon and the text center as one unit — antd's
            // .ant-alert-message is an inline span, which text-align ignores.
            className="flex w-full items-center justify-center gap-2 text-center text-[13px] leading-5"
            style={isWarning ? { color: WARNING_TONE.text } : undefined}
          >
            <LuMegaphone size={20} className="shrink-0" color={isWarning ? "#d48806" : undefined} />
            <span>
              {title && <span className="mr-1.5 font-semibold">{title}:</span>}
              {message}
              {cta?.href && cta?.label && (
                <>
                  {ctaPrefix && <span className="ml-1.5">{ctaPrefix}</span>}
                  <a
                    href={cta.href}
                    // mailto: must open in the same context — a _blank target leaves
                    // an empty tab behind after the mail client takes over.
                    target={cta.href.startsWith("http") ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    className="ml-1 font-semibold underline underline-offset-2"
                    style={isWarning ? { color: WARNING_TONE.text } : undefined}
                  >
                    {cta.label}
                  </a>
                </>
              )}
            </span>
          </span>
        }
      />
    </div>
  );
};

export default AnnouncementBanner;
